"use client"
import Image from 'next/image';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import { useState } from 'react';
// Ui components
import HeaderTitle from '@/components/reusebale-components/HeaderTitle';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Spinner } from '@/components/ui/spinner';
import { ChevronDown, Mail, MapPin, Phone } from 'lucide-react';

// custom components
import { ProjectCard } from '@/components/pages/company/ProjectCard';
import { AboutSection } from '@/components/reusebale-components/AboutSection';
import { BannerCarousel } from '@/components/reusebale-components/bannerCarousel';
import { MissionAndVission } from '@/components/reusebale-components/MissionAndVission';
import { TeamSection } from '@/components/reusebale-components/TeamSection';


// types
import { BannerData } from '@/types/banner';
import { ParentCompanyData } from '@/types/companies';
import { PhilosophyData } from '@/types/philosophy';
import { ProductData } from '@/types/product';
import { SocialMediaData } from '@/types/socialMedia';

// data fetching endpoints, SWR and types
import endPoints from '@/lib/apiEndPoints';
import { useFetchSWR } from '@/lib/swrConfig';
import { bannerEnum as be } from '@/lib/utils';
import { AboutData } from '@/types/about';
import { FaqData as Faqtype } from '@/types/faq';

export default function CompanyDetailsPage() {

    const { slug } = useParams<{ slug: string }>();
    // get company data
    const { data: companyData } = useFetchSWR(endPoints.GET_COMPANY(slug));
    const company: ParentCompanyData = companyData?.status === 200 ? companyData.data  : {};
    // banner data
    const { data: bannerData, isLoading } = useFetchSWR(endPoints.BANNER_COMPANY(be.COMPANY,company?.id));
    const banner: BannerData = bannerData?.status === 200 ? bannerData.data : {};

    // about section content
    const { data: aboutData } = useFetchSWR(endPoints.ABOUT(company?.id));
    const about: AboutData = aboutData?.status === 200 ? aboutData.data  : {};

    // faq 
    const { data: faqData } = useFetchSWR(endPoints.FAQ(company?.id));
    const faqs = faqData?.status === 200 ? faqData.data  : [];

    // company products 
    const { data: productRes } = useFetchSWR(endPoints.COMPANY_PRODUCTS(company?.id));
    const products = productRes?.status === 200 ? productRes.data  : [];

    // company social media 
    const { data: socialDataRes } = useFetchSWR(endPoints.GET_SOCIAL(company?.id));
    const socialMedias = socialDataRes?.status === 200 ? socialDataRes.data  : [];

    // company team members
    const { data: teamRes } = useFetchSWR(endPoints.DIRECTORS(company?.id));
    const teamMembers = teamRes?.status === 200 ? teamRes.data  : [];

    // mission and vision
    const { data: missionRes } = useFetchSWR(endPoints.PHILOSOPHIES(1,company?.id));
    const mission: PhilosophyData = missionRes?.status === 200 ? missionRes.data  : {};

    const { data: visionRes } = useFetchSWR(endPoints.PHILOSOPHIES(2,company?.id));
    const vision: PhilosophyData = visionRes?.status === 200 ? visionRes.data  : {};

    // about section img
    const aboutImg = about?.media?.[0]?.url ;
   
    

    // project data 
    const INITIAL_VISIBLE_COUNT = 3;
    const LOAD_MORE_COUNT = 3;
    const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_COUNT);

    const handleSeeMore = () => {
        setVisibleCount(prevCount => prevCount + LOAD_MORE_COUNT);
    };

    const showSeeMoreButton = visibleCount < products.length;

    
    const hasMissionOrVision =
    (mission?.content?.trim()?.length ?? 0) > 0 ||
    (vision?.content?.trim()?.length ?? 0) > 0;

    return (
         <>
        {/* Banner Carousel */}
            {
                isLoading ? (
                    <div className="h-screen flex items-center justify-center">
                    <Spinner />
                    </div>
                ) : (
                    <BannerCarousel
                    images={banner?.media?.map((media) => media.url)}
                    title={banner?.title}
                    description={banner?.details}
                    />
                )
            }

            <div className="container ">

                {company?.name &&(
                    <div className='pt-6 md:pt-8 lg:pt-16'>
                        <HeaderTitle title={company.name}
                            url="" 
                            btnText=""

                        />
                    </div>
                )}

                {/* About */}
                {(about.title || about.details || aboutImg) && (
                    <div className="pb-6 md:pb-8 lg:pb-16 ">
                        <AboutSection
                            title={about?.title}
                            description={about?.details}
                            image={aboutImg || '/default.jpg'}
                        />
                    </div>
                )}
                {(about.title || about.details || aboutImg)  &&(
                    <Separator />
                )}

                {/* FAQ */}
                {
                    faqs?.length > 0 &&(
                        <section className="py-6 md:py-8 lg:py-16" >
                            <div className="bg-primary px-6 py-8 shadow-none border-0 rounded-xl">
                                <h2 className='text-lg lg:text-2xl font-bold text-center text-white '>Core Values</h2>
                                <div className="grid divide-y divide-gray-300  mx-auto ">
                                    {faqs?.map((faq: Faqtype) => (
                                        <div key={faq.id} className="py-5 text-white ">
                                            <details className="group">                               
                                                <summary className="flex justify-between items-center font-bold cursor-pointer nolist">
                                                    <span className="text-white text-sm lg:text-base">{faq.title}</span>
                                                    <span className="transition group-open:rotate-180">
                                                <svg fill="none" height="20" width="20" shapeRendering="geometricPrecision" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" viewBox="0 0 24 24" ><path d="M6 9l6 6 6-6"></path>
                                                </svg>
                                            </span>
                                                </summary>
                                                <p className="text-gray-100 mt-3 group-open:animate-fadeIn text-xs sm:text-sm">
                                                {faq.details}
                                                </p>
                                            </details>
                                        </div>
                                    ))}
            
                                </div>
                            </div>
                        </section>
                    )
                }
                {faqs.length > 0 &&(<Separator  />)}

                {/* company mission and vision  */}
                {hasMissionOrVision && (
                    <div className="py-6 md:py-8 lg:py-16">
                        <MissionAndVission vision={vision} mission={mission} />
                    </div>
                    )}
                
                { ( mission || vision) &&(
                    <Separator  />
                )}

                {/* projects/services section */}
                {
                    products.length > 0 &&(
                        <section className="py-6 md:py-8 lg:py-16" >
                            <div className=" mx-auto ">
                                <h2 className={`text-xl sm:text-2xl font-bold text-center text-gray-800 mb-12 `}>
                                    Projects/Service/Products
                                </h2>

                                <div className="space-y-6">
                                    {products.slice(0, visibleCount).map((p: ProductData ) => (
                                        <ProjectCard key={p.id} project={p} />
                                    ))}
                                </div>

                                {showSeeMoreButton && (
                                    <div className="text-center mt-12">
                                        <Button
                                            onClick={handleSeeMore}
                                            className="bg-primary  text-white rounded-full px-6 py-3 hover:bg-prime-hover transition-colors"
                                        >
                                            See more
                                            <ChevronDown size={18} />
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </section>
                    )
                }

                {products.length > 0 &&(<Separator />)}

                {/* team  */}
                { teamMembers.length > 0 &&(
                    <section className='py-6 md:py-8 lg:py-16 '>
                        <TeamSection 
                            isparent={false}
                            title="Team Members"
                            members={teamMembers}
                        /> 
                    </section>
                )}

                {teamMembers.length > 0 &&(<Separator />)}
                
                {/* Contact section */}
                <div className="py-6 md:py-8 lg:py-16">
                    <h2 className="text-lg md:text-xl lg:text-2xl font-bold text-center">
                        Contact Now
                    </h2>
                    {/* Contact details */}
                    <div className=" grid grid-cols-1 md:grid-cols-2 items-center gap-8 md:gap-4 py-6 md:py-8 lg:py-16">
                        
                        
                        {/* Contact details */}
                        <div className='flex flex-col gap-4'>
                            {/* company address */}
                            <div className={`flex gap-4 ${company.address ? '' : 'hidden'}`}>
                                <span>
                                    <MapPin size={30} />
                                </span>
                                <div className=''>
                                    <h4 className='text-xl mb-2 font-bold'>Address</h4>
                                    <p className='text-sm'>{company.address}</p>
                                </div>
                            </div>
                            {/* Contact email */}
                            <div className={`flex gap-4 ${company.emails?.length > 0 ? '' : 'hidden'}`}>
                                <Mail size={30} />
                                <div>
                                    <h4 className='text-xl mb-2 font-bold'>Email</h4>
                                {/* Email content */}
                                {company.emails?.map((email, index) => (
                                    
                                    <Link rel="preload"  prefetch key={index} href={`mailto:${email}`} className='block text-sm'>
                                        {email}
                                    </Link>
                
                                ))}
                                </div>
                            </div>
                            {/* Contact phone number */}
                            <div className={`flex gap-4 ${company.phones?.length > 0 ? '' : 'hidden'}`}>
                                <Phone size={30} />
                                <div>
                                <h4 className='text-xl mb-2 font-bold'>Phone</h4>

                                {/* Phone numbers */}
                                {company.phones?.map((phone, index) => (
                                    <Link rel="preload"  prefetch key={index} href={`tel:${phone}`} className='block text-sm'>
                                        {phone}
                                    </Link>
                                    
                                ))}
                                </div>

                            </div>
                            
                        </div>

                        {/* Map Image */}
                        <Card className="overflow-hidden p-0 aspect-3/2">
                            <iframe src={company.map_url ||"https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d116833.95338886735!2d90.419689!3d23.780840500000014!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3755b8b087026b81%3A0x8fa563bbdd5904c2!2sDhaka!5e0!3m2!1sen!2sbd!4v1758105865951!5m2!1sen!2sbd"}
                            title="map" width="100%" height="100%"   loading="eager">
                            </iframe>
                        </Card>

                    </div>

                    {/* social media content */}
                    <div className={`flex flex-col lg:flex-row items-center justify-center gap-6 lg:gap-8 ${socialMedias?.length > 0 ? '' : 'hidden'}`}>
                        <h5 className='text-xl md:text-2xl font-bold mb-3 md:mb-0'>
                            Also reach us in:
                        </h5>
                        {/* social media links and icons */}

                        <div className='flex flex-wrap gap-2 md:gap-5'>
                            {socialMedias?.map((s:SocialMediaData) => (
                            <Link rel="preload"  prefetch key={s.id} href={s.url}>
                                <div className='border border-blue-400 p-1 rounded-md h-full'>
                                    <Image 
                                        src={s?.media?.[0]?.url || 'logo.png'}
                                        alt={s.name}
                                        width={30}
                                        height={30}
                                    />
                                </div>
                            </Link>
                            
                        ))}
                        </div>

                    </div>
   
                </div>
            </div>
        </>
    );
}
